import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Space,
  Tag,
  Typography,
  message,
  Select,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  rejectionService,
  type RejectionReasonDefinitionDTO,
} from '@/services/rejection.service';

const { Title, Text } = Typography;

export const RejectionReasonsSettingsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [editing, setEditing] = useState<RejectionReasonDefinitionDTO | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['rejection-definitions'],
    queryFn: () => rejectionService.listDefinitions(),
  });

  const createMutation = useMutation({
    mutationFn: rejectionService.createDefinition,
    onSuccess: () => {
      message.success('Created');
      queryClient.invalidateQueries({ queryKey: ['rejection-definitions'] });
      setCreateOpen(false);
      createForm.resetFields();
    },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax.response?.data?.message || ax.message || 'Failed');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof rejectionService.updateDefinition>[1] }) =>
      rejectionService.updateDefinition(id, body),
    onSuccess: () => {
      message.success('Updated');
      queryClient.invalidateQueries({ queryKey: ['rejection-definitions'] });
      setEditing(null);
      editForm.resetFields();
    },
    onError: (e: unknown) => {
      const ax = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(ax.response?.data?.message || ax.message || 'Failed');
    },
  });

  const openEdit = (r: RejectionReasonDefinitionDTO) => {
    setEditing(r);
    editForm.setFieldsValue({
      name: r.name,
      category: r.category ?? undefined,
      module_filter: r.module_filter ?? [],
      document_type_filter: r.document_type_filter ?? [],
      is_active: r.is_active,
      requires_additional_remarks: r.requires_additional_remarks,
      is_other_bucket: r.is_other_bucket,
      sort_order: r.sort_order,
    });
  };

  const columns: ColumnsType<RejectionReasonDefinitionDTO> = [
    { title: 'Code', dataIndex: 'reason_code', width: 140, render: (t) => <Tag>{t}</Tag> },
    { title: 'Name', dataIndex: 'name' },
    { title: 'Category', dataIndex: 'category', width: 120, render: (v) => v || '—' },
    {
      title: 'Document types',
      dataIndex: 'document_type_filter',
      ellipsis: true,
      render: (v: string[] | null) =>
        v?.length ? (
          <Text ellipsis title={v.join(', ')}>
            {v.join(', ')}
          </Text>
        ) : (
          <Text type="secondary">All</Text>
        ),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      width: 80,
      render: (v: boolean) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: 'Actions',
      key: 'a',
      width: 100,
      render: (_, r) => (
        <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
          Edit
        </Button>
      ),
    },
  ];

  const renderCreateForm = () => (
    <Form
      form={createForm}
      layout="vertical"
      onFinish={(values) => {
        createMutation.mutate({
          reason_code: values.reason_code,
          name: values.name,
          category: values.category || null,
          module_filter: values.module_filter?.length ? values.module_filter : null,
          document_type_filter: values.document_type_filter?.length ? values.document_type_filter : null,
          is_active: values.is_active !== false,
          requires_additional_remarks: !!values.requires_additional_remarks,
          is_other_bucket: !!values.is_other_bucket,
          sort_order: values.sort_order ?? 0,
        });
      }}
    >
      <Form.Item name="reason_code" label="Reason code" rules={[{ required: true }]}>
        <Input placeholder="e.g. MISSING_INFO" />
      </Form.Item>
      <Form.Item name="name" label="Display name" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item name="category" label="Category">
        <Input placeholder="Optional grouping" />
      </Form.Item>
      <Form.Item
        name="document_type_filter"
        label="Limit to document types (entity_type)"
        tooltip="Empty = applies to all types. Use tags matching backend entity_type strings (e.g. purchase_order)."
      >
        <Select mode="tags" placeholder="Type and press Enter" tokenSeparators={[',']} />
      </Form.Item>
      <Form.Item name="module_filter" label="Module filter (optional)">
        <Select mode="tags" placeholder="Optional module codes" tokenSeparators={[',']} />
      </Form.Item>
      <Form.Item name="sort_order" label="Sort order">
        <InputNumber min={0} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="is_active" label="Active" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item name="requires_additional_remarks" label="Requires additional remarks" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item name="is_other_bucket" label='Treat as "Other" (remarks required)' valuePropName="checked">
        <Switch />
      </Form.Item>
    </Form>
  );

  const renderEditForm = () => (
    <Form
      form={editForm}
      layout="vertical"
      onFinish={(values) => {
        if (!editing) return;
        updateMutation.mutate({
          id: editing.id,
          body: {
            name: values.name,
            category: values.category || null,
            module_filter: values.module_filter?.length ? values.module_filter : null,
            document_type_filter: values.document_type_filter?.length ? values.document_type_filter : null,
            is_active: values.is_active,
            requires_additional_remarks: values.requires_additional_remarks,
            is_other_bucket: values.is_other_bucket,
            sort_order: values.sort_order,
          },
        });
      }}
    >
      <Form.Item name="name" label="Display name" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item name="category" label="Category">
        <Input placeholder="Optional grouping" />
      </Form.Item>
      <Form.Item
        name="document_type_filter"
        label="Limit to document types (entity_type)"
        tooltip="Empty = applies to all types."
      >
        <Select mode="tags" placeholder="Type and press Enter" tokenSeparators={[',']} />
      </Form.Item>
      <Form.Item name="module_filter" label="Module filter (optional)">
        <Select mode="tags" placeholder="Optional module codes" tokenSeparators={[',']} />
      </Form.Item>
      <Form.Item name="sort_order" label="Sort order">
        <InputNumber min={0} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item name="is_active" label="Active" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item name="requires_additional_remarks" label="Requires additional remarks" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item name="is_other_bucket" label='Treat as "Other" (remarks required)' valuePropName="checked">
        <Switch />
      </Form.Item>
    </Form>
  );

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={4} style={{ marginBottom: 0 }}>
            Rejection reasons
          </Title>
          <Text type="secondary">Master list used when rejecting documents in universal approval and validated APIs.</Text>
        </div>
        <Card>
          <Space style={{ marginBottom: 16 }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              Add reason
            </Button>
          </Space>
          <Table<RejectionReasonDefinitionDTO>
            rowKey="id"
            loading={isLoading}
            columns={columns}
            dataSource={rows}
            pagination={{ pageSize: 25, showSizeChanger: true }}
          />
        </Card>
      </Space>

      <Modal
        title="Add rejection reason"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          createForm.resetFields();
        }}
        confirmLoading={createMutation.isPending}
        onOk={() => createForm.submit()}
        destroyOnClose
        width={560}
      >
        {renderCreateForm()}
      </Modal>

      <Modal
        title={`Edit: ${editing?.reason_code ?? ''}`}
        open={!!editing}
        onCancel={() => {
          setEditing(null);
          editForm.resetFields();
        }}
        confirmLoading={updateMutation.isPending}
        onOk={() => editForm.submit()}
        destroyOnClose
        width={560}
      >
        {renderEditForm()}
      </Modal>
    </div>
  );
};
